import { useTranslation } from "react-i18next";

import { CardContainer } from "@/components/card-container";
import { ErrorCard } from "@/components/error-card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { parseError } from "@/lib/error-handler";
import {
  getConfirmedVisitDate,
  getVisitPayments,
  isVisitConfirmed,
} from "@/lib/visit-schedule";

import { VisitConfirmedDate } from "../../components/visit-confirmed-date";
import { VisitPaymentsList } from "../../components/visit-payments-list";
import { VisitTimelineBullet } from "../../components/visit-timeline-bullet";
import { useVisitSchedule } from "../../hooks/use-visit-schedule";

export const VisitsPage = () => {
  const { t } = useTranslation();
  const { visits, visitsLoading, visitsError, refetchVisits } =
    useVisitSchedule();

  const renderPageContent = () => {
    if (visitsError) {
      const { message, title } = parseError(visitsError);
      return (
        <ErrorCard
          title={t(title)}
          message={t(message)}
          buttonLabel={t("retry")}
          onButtonClick={() => refetchVisits()}
        />
      );
    }

    if (visitsLoading) {
      return (
        <div
          className="space-y-8"
          role="status"
          aria-label="Loading visits timeline"
        >
          {Array.from(new Array(3)).map((_, index) => (
            <div className="relative" key={`loading-${index}`}>
              {index < 2 && (
                <div className="absolute left-3.5 top-7 h-full w-px bg-border" />
              )}
              <div className="flex gap-4">
                <Skeleton className="h-7 w-7 shrink-0 rounded-full" />
                <div className="flex flex-col gap-2">
                  <Skeleton className="h-5 w-32" />
                  <Skeleton className="h-4 w-48" />
                </div>
              </div>
            </div>
          ))}
        </div>
      );
    }

    return (
      <div className="space-y-8" data-test-id="visits-timeline">
        {visits.map((visit, index) => {
          const confirmed = isVisitConfirmed(visit);
          const confirmedDate = getConfirmedVisitDate(visit);
          const payments = getVisitPayments(visit);
          const hasPayments = payments.length > 0;
          return (
            <div className="relative" data-test-id="visit" key={visit.id}>
              {index < visits.length - 1 && (
                <div className="absolute left-3.5 top-7 h-full w-px bg-border" />
              )}
              <div className="flex gap-4">
                <VisitTimelineBullet confirmed={confirmed} />
                <div className="flex min-w-0 flex-1 flex-col gap-1">
                  <div className="flex items-center gap-2">
                    <h3 className="text-lg font-semibold">{visit.name}</h3>
                    {confirmed && (
                      <Badge
                        variant="outline"
                        className="border-green-200 bg-green-100 text-green-800 hover:bg-green-100"
                      >
                        {t("visits_completed")}
                      </Badge>
                    )}
                  </div>
                  {confirmedDate && (
                    <VisitConfirmedDate visitDate={confirmedDate} />
                  )}
                  {hasPayments && <VisitPaymentsList payments={payments} />}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <CardContainer>
      <CardContainer.Header>
        <CardContainer.HeaderContent>
          <CardContainer.CardTitle>{t("visits_title")}</CardContainer.CardTitle>
          <CardContainer.CardDescription>
            {t("visits_scheduleTitle")}
          </CardContainer.CardDescription>
        </CardContainer.HeaderContent>
      </CardContainer.Header>
      <CardContainer.Content>{renderPageContent()}</CardContainer.Content>
    </CardContainer>
  );
};
